import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import {
  cancelIndent, closeIndent, createIndent, fetchIndent, fetchIndents, fetchStockLevels, saveStockLevels,
} from './api';
import type { IndentFilter, LevelChange, NewIndentInput } from './types';

export function useIndents(status: IndentFilter, storeId?: number) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.indents({ status, storeId }), branchVersion],
    queryFn: () => fetchIndents(status, storeId),
  });
}

export function useIndent(id: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.indentDetail(id ?? 0), branchVersion],
    queryFn: () => fetchIndent(id as number),
    enabled: id != null,
  });
}

export function useStockLevels(storeId: number | null, belowOnly = false) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.pharmacy.stockLevels(storeId ?? 0), belowOnly, branchVersion],
    queryFn: () => fetchStockLevels(storeId as number, belowOnly),
    enabled: storeId != null,
  });
}

/** Indents, transfers and levels share keys.pharmacy.transfersAll(); the bell lists open indents. */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: keys.pharmacy.transfersAll() });
    qc.invalidateQueries({ queryKey: keys.notifications.feed() });
  };
}

export function useCreateIndent() {
  const after = useInvalidate();
  return useMutation({ mutationFn: (input: NewIndentInput) => createIndent(input), onSuccess: after });
}

export function useEndIndent() {
  const after = useInvalidate();
  return useMutation({
    mutationFn: ({ id, kind, reason }: { id: number; kind: 'cancel' | 'close'; reason: string }) =>
      (kind === 'cancel' ? cancelIndent(id, reason) : closeIndent(id, reason)),
    onSuccess: after,
  });
}

export function useSaveStockLevels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storeId, levels }: { storeId: number; levels: LevelChange[] }) => saveStockLevels(storeId, levels),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.pharmacy.transfersAll() });
      qc.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
      qc.invalidateQueries({ queryKey: keys.notifications.feed() });
    },
  });
}
